<template>
  <div ref="stackBarCanvas">
    <ClientOnly>
      <template v-if="tooltip.tooltipPosition.value.owner === tooltipId">
        <Teleport to="#teleport-tooltipchart">
          <div class="text-2xl text-Primary-800">
            <span class="font-bold">{{ hoveredData.serieId }}: </span>
            <span class="font-bold">{{ hoveredData.value }}</span>
            <span v-if="hoveredData.percentage" class="pl-2">
              {{ hoveredData.percentage }}
            </span>
          </div>

          <div class="text-sm">{{ hoveredData.childrenId }}</div>
        </Teleport>
      </template>
    </ClientOnly>

    <div class="relative" :class="showLegend ? 'md:grid grid-cols-5 ' : ''">
      <div class="col col-span-4" ref="svgwrapper">
        <svg
          ref="svg"
          class=""
          :width="availableWidth"
          :height="requiredHeight"
        >
          <!-- y axis -->
          <g id="axis" :transform="`translate(0,0)`" v-if="showLabels">
            <g
              v-for="(d, i) in heightScale.ticks(5)"
              :key="i"
              :transform="`translate(0,${maxScaleHeight - heightScale(d)})`"
            >
              <line
                :x1="axisWidth - 12"
                y1="2"
                :x2="widthOccupiedByBars + axisWidth"
                y2="2"
                :stroke="i === 0 ? '#000000' : '#D4D5E1'"
                :stroke-width="i === 0 ? '1' : '0.5'"
              />
              <text
                x="0"
                y="0"
                dy="0.4em"
                text-anchor="start"
                font-size="11px"
                class="fill-zinc-800 font-light"
              >
                {{ $formater.salaryFormater(d) }}
              </text>
            </g>
          </g>
          <g
            v-for="(serie, i) in series"
            :key="serie.id"
            :transform="`translate(${axisWidth + i * (barWidth * 1.2)},0)`"
          >
            <rect
              v-for="(d, stackIndex) in serie.children"
              :key="serie.id + stackIndex"
              :y="getYPos(stackIndex, serie.children)"
              :x="0"
              :rx="roundedRadius"
              :ry="roundedRadius"
              :data-id="d.id"
              :data-stackindex="stackIndex"
              :height="heightScale(d.value)"
              :width="barWidth"
              :fill="colorScale(d.id)"
              :class="[
                rectClass,
                hoveredLegendId !== '' && hoveredLegendId !== d.id
                  ? 'opacity-10'
                  : '',
              ]"
              @mouseover="hoveredItemHandler($event, d, serie.id)"
              @mouseleave="hoveredItemHandler($event, null)"
            />

            <template v-if="showFigures">
              <text
                v-for="(d, stackIndex) in serie.children"
                v-show="heightScale(d.value) > 40"
                :key="'text' + serie.id + stackIndex"
                class="pointer-events-none text-xs fill-white font-semibold mt-1 ml-1 overflow-x-hidden"
                :x="getYPos(stackIndex, serie.children)"
                :y="barWidth / 2"
                dy="0.25rem"
                dx="0.25rem"
                :style="{
                  height: heightScale(d) + 'px',
                  width: barWidth + 'px',
                }"
              >
                {{ $formater.salaryFormater(d.value) }}
              </text>
            </template>

            <!-- main text legend for bar-->
            <text
              v-if="showLabels"
              text-anchor="middle"
              font-size="13px"
              :x="barWidth / 2"
              :y="requiredHeight"
              :dx="'.35em'"
              class="fill-softBlack font-bold text-xs"
              :class="{
                italic: serie.id === 'Average',
                'font-thin': serie.id === 'Average',
              }"
            >
              {{ serie.id }}
            </text>
          </g>
        </svg>
      </div>
      <div class="col-span-1" v-if="showLegend">
        <div class="mt-4 flex-col justify-start">
          <!-- <InterfaceLabelBadge
            v-for="(
              catName, index
            ) in allSubCategoryNames"
            :key="index"
            :category="catName"
            parent-id="vertical-stack-evo"
            @mouseover="hoveredLegendId = catName"
            @mouseleave="hoveredLegendId = ''"
          /> -->

          <filtersToggleBadge
            v-for="(catName, index) in allSubCategoryNames"
            :key="catName"
            :category="catName"
            :enabled="true"
            :show-remove="false"
            :interactive="false"
            class="hover:brightness-125 inline mt-2"
            @_mouseover="hoveredLegendId = catName"
            @_mouseleave="hoveredLegendId = ''"
          />

          <!-- <div
            v-for="catName in allSubCategoryNames"
            :key="catName"
            class="flex justify-start gap-4"
            @mouseover="hoveredLegendId = catName"
            @mouseleave="hoveredLegendId = ''"
          >
            <div
              :style="{ backgroundColor: colorScale(catName) }"
              class="w-4 h-4 shrink-0"
            />
            <div class="text-xs">{{ catName }}</div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  scaleLinear,
  scaleOrdinal,
  sum,
  max,
  schemeTableau10,
  ascending,
} from "d3";

export default {
  setup() {
    const tooltip = useTooltip();
    const svgwrapper = ref(null);
    const { width: availableWidth, height: availableHeight } =
      useElementSize(svgwrapper);
    return { tooltip, svgwrapper, availableHeight, availableWidth };
  },
  props: {
    series: {
      type: Array,
      default: () => [
        {
          id: "2010",
          children: [
            {
              id: "1",
              value: 0.1,
              valueRelative: 0.3,
            },
            {
              id: "2",
              value: 0.2,
              valueRelative: 0.6,
            },
          ],
          value: 0.3,
        },
        {
          id: "2011",
          children: [
            {
              id: "1",
              value: 0.4,
              valueRelative: 0.7,
            },
            {
              id: "2",
              value: 0.5,
              valueRelative: 0.7,
            },
          ],
          value: 0.9,
        },
      ],
    },
    colors_cat: {
      type: Array,
      default: () => schemeTableau10,
    },

    isNotOrderable: {
      type: Boolean,
      default: false,
    },
    hasAverageSpecial: {
      type: Boolean,
      default: true,
    },
    showFigures: {
      type: Boolean,
      default: false,
    },
    axisWidth: {
      type: Number,
      default: 50,
    },
    axisHeight: {
      type: Number,
      default: 20,
    },
    requiredHeight: {
      type: Number,
      default: 475,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    baseBarWidth: {
      type: Number,
      default: 100,
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    colorScaleExternal: {
      type: Function,
      default: null,
    },
    rectClass: {
      type: String,
      default: "stroke-white stroke-2 hover:brightness-110",
    },
    roundedRadius: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      maxScaleHeight: 0,
      hoveredData: {},
      hoveredLegendId: "",
      showTeleport: false,
      tooltipId: "stacked-vertical-tooltip",
    };
  },

  computed: {
    barWidth() {
      const numberOfBars = this.series.length;
      const availableWidth = Math.max(
        this.availableWidth - this.axisWidth,
        320
      );
      // set bar width to baseBarWidth if there is space otherwise reduce bar width
      // consider a spacing of 20% between bars
      const barWidth = Math.min(
        this.baseBarWidth,
        (availableWidth * 0.8) / numberOfBars
      );
      return barWidth;
    },
    widthOccupiedByBars() {
      return this.barWidth * 1.2 * this.series.length;
    },
    spaceForLegend() {
      return this.showLegend ? 0.3 : 0;
    },
    maxSeriesData() {
      return max(this.series, (d) => d.value);
    },
    heightScale() {
      return scaleLinear()
        .domain([0, this.maxSeriesData])
        .range([0, this.maxScaleHeight])
        .clamp(true);
    },

    colorScale() {
      if (this.colorScaleExternal !== null) return this.colorScaleExternal;
      else {
        const colorArray = this.colors_cat;
        return scaleOrdinal()
          .domain(this.allSubCategoryNames)
          .range(colorArray);
      }
    },
    allSubCategoryNames() {
      const allnames = this.series.flatMap((d) => d.children.map((c) => c.id));
      // remove duplicates
      return [...new Set(allnames)].sort((a, b) => ascending(a, b));
    },
  },
  mounted() {
    this.tooltipId = this.tooltipId + Math.random().toString(36).substr(2, 9);
    this.$nextTick(() => {
      // run this code once 500ms after the component is mounted
      setTimeout(() => {
        this.maxScaleHeight = this.requiredHeight - this.axisHeight;
      }, 600);
    });
    // update maxScaleHeight when the window is resized
  },
  // destory listener when component is destroyed
  unmounted() {},

  methods: {
    getYPos(stackIndex, stackElements) {
      // get the sum of all items in the stack before the current item defined by stackIndex
      const sumOfPreviousStackElements = sum(
        stackElements.slice(0, stackIndex).map((d) => d.value)
      );
      // return the x position of the current item
      return (
        this.maxScaleHeight -
        this.heightScale(sumOfPreviousStackElements) -
        this.heightScale(stackElements[stackIndex].value)
      );
    },
    convertRemToPixels(rem) {
      if (typeof window !== "undefined")
        return (
          rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
        );
      else return rem * 16;
    },
    hoveredItemHandler(event, d, serieId) {
      if (d === null) {
        this.tooltip.hide();
      } else {
        this.hoveredData = {
          serieId,
          childrenId: d.id,
          value: this.$formater.salaryFormater(d.value),
          percentage: d.valueRelative
            ? `${this.$formater.intFormater(d.valueRelative * 100)}%`
            : "",
        };
        this.tooltip.show(event.pageX, event.pageY, this.tooltipId, "4chart");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
rect {
  transition: y 0.5s, height 0.5s, width 1s, opacity 0.2s, filter 0.4s;
}
</style>
